// Copyright (c) Move Closer 2022.

import gql from 'graphql-tag'

import { getQueryByConfig, QueryRegistryFactory } from '../../../contexts'

import { CART_FRAGMENTS_MAP } from './graphql/fragments'
import { PLACE_ORDER_QUERY_VERSIONS } from './queries/place-order.query'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (edited)
 * @author Filip Rurak <filip.rurak@movecloser.pl> (edited)
 */
export const checkoutQueries: QueryRegistryFactory = (config) => {
  const CART_FRAGMENT = getQueryByConfig(config, CART_FRAGMENTS_MAP)

  // Queries
  const placeOrderQuery = getQueryByConfig(config, PLACE_ORDER_QUERY_VERSIONS)

  return ({
    addCartItems: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation addProductsToCart($cartId: String!, $items: [CartItemInput!]!) {
          addProductsToCart(
            cartId: $cartId
            cartItems: $items
          ) {
            cart {
              ...CartFragment
            }
            user_errors {
              message
            }
          }
        }
      `,
      meta: {}
    },
    addSimpleProductsToCart: {
      type: 'mutation',
      query: gql`
          ${CART_FRAGMENT}
          mutation addSimpleProductsToCart($input: AddSimpleProductsToCartInput!) {
              addSimpleProductsToCart(
                  input: $input
              ) {
                  cart {
                      ...CartFragment
                  }
              }
          }
      `,
      meta: {}
    },
    applyCoupon: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation applyCoupon($cartId: String!, $code: String!) {
          applyCouponToCart(
            input: {
              cart_id: $cartId
              coupon_code: $code
            }
          ) {
            cart {
              ...CartFragment
            }
          }
        }
      `,
      meta: {}
    },
    applyAmGiftCardToCart: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation applyAmGiftCardToCart($input: ApplyAmGiftCardToCartInput!) {
            applyAmGiftCardToCart(
                input: $input
            ) {
                cart {
                    ...CartFragment
                }
            }
        }
      `,
      meta: {}
    },
    removeAmGiftCardFromCart: {
      type: 'mutation',
      query: gql`
          ${CART_FRAGMENT}
          mutation removeAmGiftCardFromCart($input: RemoveAmGiftCardFromCartInput!) {
              removeAmGiftCardFromCart(
                  input: $input
              ) {
                  cart {
                      ...CartFragment
                  }
              }
          }
      `,
      meta: {}
    },
    cartQuery: {
      type: 'query',
      query: gql`
        ${CART_FRAGMENT}
        query cart($cartId: String!) {
          cart(cart_id: $cartId) {
            ...CartFragment
          }
        }
      `,
      meta: {}
    },
    clearCoupons: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation removeCouponFromCart($cartId: String!) {
          removeCouponFromCart(
            input: {
              cart_id: $cartId
            }
          ) {
            cart {
              ...CartFragment
            }
          }
        }`,
      meta: {}
    },
    createEmptyCart: {
      type: 'mutation',
      query: gql`
        mutation createEmptyCart {
          createEmptyCart
        }
      `,
      meta: {
        noAuth: true
      }
    },
    getCountries: {
      type: 'query',
      query: gql`
        query countries {
          countries {
            id
            full_name_locale
          }
        }
      `,
      meta: {
        noAuth: true
      }
    },
    loadPrzelewy24PaymentMethods: {
      type: 'query',
      query: gql`
      query przelewy24PaymentMethods ($total: Float!) {
          przelewy24PaymentMethods(amount: $total) {
              active
              id,
              imgUrl,
              name,
              mobileImgUrl,
          },
      },
      `,
      meta: {
        noAuth: false
      }
    },
    loadCustomerCart: {
      type: 'query',
      query: gql`
        ${CART_FRAGMENT}
        query customerCart {
          customerCart {
            ...CartFragment
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    loadSimpleCart: {
      type: 'query',
      query: gql`query queryCart($cartId: String!){
        cart(cart_id: $cartId) {
          items {
            id
            uid
            product {
              name
              sku
            }
            quantity
          }
          prices {
            subtotal_including_tax {
              value
              currency
            }
            subtotal_excluding_tax {
              value
              currency
            }
          }
        }
      }`,
      meta: {
        noAuth: true
      }
    },
    mergeCarts: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation mergeCarts($source: String!, $destination: String!){
          mergeCarts(
            source_cart_id: $source,
            destination_cart_id: $destination
          ) {
            ...CartFragment
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    registerPrzelewy24BlikTransaction: {
      type: 'mutation',
      query: gql`
        mutation registerPrzelewy24BlikTransaction ($cartId: String!, $saveAlias: Boolean) {
          registerPrzelewy24BlikTransaction(input: { cart_id: $cartId, save_alias: $saveAlias }) {
            token
            transaction
          }
        }
      `,
      meta: {
        noAuth: false
      }
    },
    registerPrzelewy24GooglePayTransaction: {
      type: 'mutation',
      query: gql`
          mutation registerPrzelewy24GooglePayTransaction ($cartId: String!, $tokenObject: String!) {
              registerPrzelewy24GooglePayTransaction(input: { cart_id: $cartId, token_object: $tokenObject }) {
                  token
                  transaction
              }
          }
      `,
      meta: {
        noAuth: false
      }
    },
    setGuestEmail: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setGuestEmailOnCart($cartId: String!, $email: String!) {
          setGuestEmailOnCart(
            input: {
              cart_id: $cartId,
              email: $email
            }
          ) {
            cart {
              ...CartFragment
            }
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    setBillingAddress: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setBillingAddressOnCart($cartId: String!, $address: BillingAddressInput!) {
          setBillingAddressOnCart(
            input: {
              cart_id: $cartId,
              billing_address: $address
            }
          ) {
            cart { ...CartFragment }
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    setInPostParcel: {
      type: 'mutation',
      query: gql`
        mutation setInPostLockerNumberOnCart($cartId: String!, $parcelId: String!) {
          setInPostLockerNumberOnCart(
            input: {
              cart_id: $cartId,
              locker_no: $parcelId
            }
          ) {
            cart { id }
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    setDhlPopLockerNumberOnCart: {
      type: 'mutation',
      query: gql`
        mutation setDhlPopLockerNumberOnCart($cartId: String!, $parcelId: String!) {
          setDhlPopLockerNumberOnCart (
              input: {
                  cart_id: $cartId,
                  locker_no: $parcelId
              }
          ) {
            cart { id }
          }
        }
      `,
      meta: {
        noAuth: false
      }
    },
    setShippingAddress: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setShippingAddressesOnCart($cartId: String!, $address: ShippingAddressInput!) {
          setShippingAddressesOnCart(
            input: {
              cart_id: $cartId,
              shipping_addresses: [$address]
            }
          ) {
            cart { ...CartFragment }
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    setShippingDetails: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setDeliveryDetailsOnCart($input: SetDeliveryDetailsOnCartInput!) {
          setDeliveryDetailsOnCart(input: $input) {
            cart { ...CartFragment }
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    setShippingMethod: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setShippingMethodsOnCart($cartId: String!, $shippingMethod: ShippingMethodInput!) {
          setShippingMethodsOnCart(
            input: {
              cart_id: $cartId,
              shipping_methods: [$shippingMethod]
            }
          ) {
            cart { ...CartFragment }
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    setPaymentMethod: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setPaymentMethodOnCart($cartId: String!, $paymentMethod: PaymentMethodInput!) {
          setPaymentMethodOnCart(
            input: {
              cart_id: $cartId,
              payment_method: $paymentMethod
            }
          )  {
            cart { ...CartFragment }
          }
        }`,
      meta: {
        noAuth: false
      }
    },
    placeOrder: {
      type: 'mutation',
      query: placeOrderQuery,
      meta: {
        noAuth: false
      }
    },
    removeFromCart: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation removeItemFromCart($cartId: String!, $uid: ID!) {
          removeItemFromCart(
            input: {
              cart_id: $cartId,
              cart_item_uid: $uid
            }
          ) {
            cart {
              ...CartFragment
            }
          }
        }`,
      meta: {}
    },
    updateCartItem: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation updateCartItems($cartId: String!, $uid: ID!, $quantity: Float!) {
          updateCartItems(
            input: {
              cart_id: $cartId
              cart_items: [
                {
                  quantity: $quantity
                  cart_item_uid: $uid
                }
              ]
            }
          ) {
            cart {
              ...CartFragment
            }
          }
        }
      `,
      meta: {}
    },
    updateCartItems: {
      type: 'mutation',
      query: gql`
          ${CART_FRAGMENT}
          mutation updateCartItems($cartId: String!, $updatedItems: [CartItemUpdateInput]!) {
              updateCartItems(
                  input: {
                      cart_id: $cartId
                      cart_items: $updatedItems
                  }
              ) {
                  cart {
                      ...CartFragment
                  }
              }
          }
      `,
      meta: {}
    },
    setFlightNumberOnCart: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setFlightNumberOnCart($cartId: String!, $flightNo: String!) {
            setFlightNumberOnCart(
                input: {
                    cart_id: $cartId,
                    flight_number: $flightNo
                }
            ) {
                cart {
                    ...CartFragment
                }
            }
        }
      `,
      meta: {}
    },
    setSelectedAddressIdOnCart: {
      type: 'mutation',
      query: gql`
        ${CART_FRAGMENT}
        mutation setSelectedAddressIdOnCart($cartId: String!, $addressId: Int!) {
          setSelectedAddressIdOnCart(
            input: {
              cart_id: $cartId,
              selected_address_id: $addressId
            }
          ) {
            cart {
              ...CartFragment
            }
          }
        }
      `,
      meta: {}
    },
    setIsSignupRequestedOnCart: {
      type: 'mutation',
      query: gql`
          ${CART_FRAGMENT}
          mutation setIsSignupRequestedOnCart($cartId: String!, $isSignupRequested: Boolean!) {
              setIsSignupRequestedOnCart(
                  input: {
                      cart_id: $cartId,
                      is_signup_requested: $isSignupRequested
                  }
              ) {
                  cart {
                      ...CartFragment
                  }
              }
          }
      `,
      meta: {}
    },
    getOrderStatusByOrderNumber: {
      type: 'query',
      query: gql`
        query getOrderStatusByOrderNumber($orderNumber: String!) {
          getOrderStatusByOrderNumber(orderNumber: $orderNumber) {
            entityId
            orderNumber
            status
          }
        }
      `,
      meta: {
        noAuth: false
      }
    }
  })
}
